import { Archive, ClipboardList, Building2, Database, Inbox, LogOut, Menu, X } from "lucide-react";
import * as React from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { ContaDialog } from "@/components/conta/ContaDialog";
import { usePendentes } from "@/hooks/queries";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

const ITENS_TRABALHO = [
  { para: "/empresas", rotulo: "Empresas FS", icone: Building2 },
  { para: "/base-tax", rotulo: "Base TAX", icone: Database },
];

/** As três frentes de venda, agrupadas sob a marca FaStax. */
const ITENS_FASTAX = [
  { para: "/vendas-produtora", rotulo: "Produtora" },
  { para: "/vendas-ece", rotulo: "ECE" },
  { para: "/vendas-distribuidora", rotulo: "Distribuidora" },
];

/** A régua da marca, em pé: verde no pé, lima no meio, amarelo junto ao nome. */
const REGUA_VERTICAL = "linear-gradient(0deg,#1a7753,#79ab2b 55%,#f7d21e)";

const classeItem = ({ isActive }: { isActive: boolean }) =>
  cn(
    "relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-[0.84rem] font-bold transition-all duration-200",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-menta",
    "before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-r-full before:transition-all before:duration-300 before:ease-suave",
    isActive
      ? "bg-white/[0.09] text-white before:bg-fs-menta before:shadow-[0_0_12px_rgba(149,213,178,0.55)]"
      : "text-fs-claro/75 before:scale-y-0 before:bg-fs-menta/60 hover:translate-x-0.5 hover:bg-white/[0.06] hover:text-fs-areia hover:before:scale-y-100",
  );

/** Subitem do grupo FaStax: mesmo corpo, marcador no amarelo da marca. */
const classeSubitem = ({ isActive }: { isActive: boolean }) =>
  cn(
    "relative flex items-center rounded-md px-3 py-1.5 text-[0.82rem] font-bold transition-all duration-200",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fx-amarelo/70",
    isActive
      ? "bg-white/[0.08] text-fx-creme shadow-[inset_2px_0_0_#f7d21e]"
      : "text-fs-claro/75 hover:translate-x-0.5 hover:bg-white/[0.06] hover:text-fx-creme",
  );

function Secao({ titulo, children }: { titulo?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      {titulo && (
        <p className="px-3 pb-1 text-[10px] font-extrabold uppercase tracking-[0.16em] text-fs-claro/45">
          {titulo}
        </p>
      )}
      {children}
    </div>
  );
}

/**
 * Grupo FASTAX: o nome da marca no topo e, ao lado das três frentes, uma régua
 * vertical nas cores da marca — o colchete da versão horizontal, em pé. Acende
 * quando uma das frentes está ativa.
 */
function GrupoFastax() {
  const { pathname } = useLocation();
  const ativo = ITENS_FASTAX.some((i) => pathname.startsWith(i.para));

  return (
    <div role="group" aria-label="FASTAX" className="group/fx space-y-1.5">
      <p className="px-3 text-[0.8rem] font-extrabold leading-none tracking-[0.14em] text-fs-areia">
        <span className="text-fx-amarelo">F</span>A<span className="text-fx-amarelo">S</span>TAX
      </p>
      <div className="relative ml-3 space-y-0.5 pl-2.5">
        <span
          aria-hidden
          className={cn(
            "absolute inset-y-1 left-0 w-[3px] rounded-full transition-opacity duration-300",
            ativo ? "opacity-100" : "opacity-55 group-hover/fx:opacity-85",
          )}
          style={{ background: REGUA_VERTICAL }}
        />
        {ITENS_FASTAX.map((i) => (
          <NavLink key={i.para} to={i.para} className={classeSubitem}>
            {i.rotulo}
          </NavLink>
        ))}
      </div>
    </div>
  );
}

/**
 * Grupo FASTESTER: mesma moldura do FaStax (nome da marca e régua vertical), mas
 * ainda sem frentes — só o aviso de que está em desenvolvimento.
 */
function GrupoFastester() {
  return (
    <div role="group" aria-label="FASTESTER" className="space-y-1.5">
      <p className="px-3 text-[0.8rem] font-extrabold leading-none tracking-[0.14em] text-fs-areia">
        <span className="text-fx-amarelo">F</span>A<span className="text-fx-amarelo">S</span>TESTER
      </p>
      <div className="relative ml-3 pl-2.5">
        <span
          aria-hidden
          className="absolute inset-y-1 left-0 w-[3px] rounded-full opacity-40"
          style={{ background: REGUA_VERTICAL }}
        />
        <p className="px-3 py-1.5 text-[0.82rem] font-semibold italic text-fs-claro/55">Em desenvolvimento...</p>
      </div>
    </div>
  );
}

/** Iniciais do usuário para o avatar — "Ana Paula Reis" vira "AR". */
function iniciais(nome: string) {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return "?";
  return (partes[0][0] + (partes.length > 1 ? partes[partes.length - 1][0] : "")).toUpperCase();
}

export function AppShell() {
  const { usuario, sair, tipoAprovacao } = useAuth();
  const { data: pendentes } = usePendentes(Boolean(tipoAprovacao));
  const [contaAberta, setContaAberta] = React.useState(false);
  const [menuAberto, setMenuAberto] = React.useState(false);
  const { pathname } = useLocation();

  // No celular o menu é uma gaveta: fecha ao navegar.
  React.useEffect(() => setMenuAberto(false), [pathname]);

  return (
    <div className="flex min-h-screen flex-col">
      {/* Cabeçalho enxuto: marca à esquerda, conta e saída à direita. A navegação mora no menu lateral. */}
      <header className="nao-imprimir sticky top-0 z-40 bg-fs-escuro text-fs-areia shadow-alta">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(60rem_18rem_at_18%_-40%,rgba(141,200,145,0.22),transparent_70%)]"
        />
        <div className="relative flex h-14 items-center justify-between gap-4 px-4 lg:px-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setMenuAberto((v) => !v)}
              aria-label={menuAberto ? "Fechar menu" : "Abrir menu"}
              aria-expanded={menuAberto}
              className="grid size-9 place-items-center rounded-lg text-fs-areia transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-menta lg:hidden"
            >
              {menuAberto ? <X className="size-5" /> : <Menu className="size-5" />}
            </button>
            <h1 className="text-[1.05rem] font-extrabold leading-tight tracking-tight">
              OLÉ{" "}
              <span className="hidden font-medium text-fs-claro sm:inline">· Novas Operações Etanol</span>
            </h1>
          </div>

          {/* Conta e saída numa cápsula só: mesma altura, mesmo vidro, separadas por um fio. */}
          <div className="flex items-center gap-1 rounded-full bg-white/[0.06] p-1 ring-1 ring-inset ring-white/[0.12] backdrop-blur-sm">
            {usuario && (
              <>
                <button
                  type="button"
                  onClick={() => setContaAberta(true)}
                  title="Minha conta: senha, e-mail e usuários"
                  className="group flex h-9 items-center gap-2.5 rounded-full py-1 pl-1 pr-3.5 transition-colors duration-200 hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-menta"
                >
                  <span
                    aria-hidden
                    className="grid size-7 place-items-center rounded-full bg-fs-menta text-[11px] font-extrabold text-fs-escuro shadow-[0_0_0_2px_rgba(149,213,178,0.18)] transition-shadow duration-200 group-hover:shadow-[0_0_0_3px_rgba(149,213,178,0.3),0_0_14px_rgba(149,213,178,0.45)]"
                  >
                    {iniciais(usuario.nome)}
                  </span>
                  <span className="hidden text-left leading-tight sm:block">
                    <span className="block text-xs font-bold text-white">{usuario.nome}</span>
                    <span className="block text-[10px] font-semibold uppercase tracking-[0.12em] text-fs-claro">
                      {usuario.papel}
                    </span>
                  </span>
                </button>
                <span aria-hidden className="h-5 w-px bg-white/15" />
              </>
            )}
            <button
              type="button"
              onClick={sair}
              title="Sair"
              className="flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-bold text-fs-areia/80 transition-colors duration-200 hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fs-menta [&_svg]:size-3.5 [&_svg]:transition-transform [&_svg]:duration-200 hover:[&_svg]:translate-x-0.5"
            >
              <LogOut aria-hidden />
              Sair
            </button>
          </div>
        </div>
      </header>

      <ContaDialog aberto={contaAberta} aoMudar={setContaAberta} />

      <div className="flex flex-1">
        {menuAberto && (
          <div
            aria-hidden
            onClick={() => setMenuAberto(false)}
            className="nao-imprimir fixed inset-0 top-14 z-30 bg-black/40 lg:hidden"
          />
        )}
        {/* Menu lateral fixo no desktop; gaveta sobre o conteúdo no celular. */}
        <aside
          className={cn(
            "nao-imprimir fixed bottom-0 left-0 top-14 z-30 flex w-64 flex-col overflow-y-auto bg-fs-escuro px-3 py-5 text-fs-areia shadow-alta transition-transform duration-300 ease-suave",
            "lg:sticky lg:h-[calc(100vh-3.5rem)] lg:w-60 lg:shrink-0 lg:translate-x-0 lg:self-start lg:shadow-none",
            menuAberto ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <nav aria-label="Principal" className="flex flex-col gap-6">
            <Secao>
              {ITENS_TRABALHO.map(({ para, rotulo, icone: Icone }) => (
                <NavLink key={para} to={para} className={classeItem}>
                  <Icone aria-hidden className="size-4 shrink-0 opacity-80" />
                  {rotulo}
                </NavLink>
              ))}
            </Secao>
            <GrupoFastax />
            <GrupoFastester />
            <Secao titulo="Operações">
              {tipoAprovacao && (
                <NavLink to="/pendentes" className={classeItem}>
                  <Inbox aria-hidden className="size-4 shrink-0 opacity-80" />
                  Pendentes
                  {pendentes && pendentes.length > 0 && (
                    <span
                      className="ml-auto rounded-full bg-fs-menta px-1.5 py-0.5 text-[0.68rem] leading-none text-fs-escuro"
                      aria-label={`${pendentes.length} pendentes`}
                    >
                      {pendentes.length}
                    </span>
                  )}
                </NavLink>
              )}
              <NavLink to="/minhas-operacoes" className={classeItem}>
                <ClipboardList aria-hidden className="size-4 shrink-0 opacity-80" />
                Minhas operações
              </NavLink>
              <NavLink to="/repositorio" className={classeItem}>
                <Archive aria-hidden className="size-4 shrink-0 opacity-80" />
                Repositório
              </NavLink>
            </Secao>
          </nav>
          {/* Milharal em traço no pé do menu: fundo transparente, some no verde da sidebar. */}
          <div aria-hidden className="pointer-events-none -mx-3 mt-auto select-none pt-8">
            <img
              src="/milho-sidebar.png"
              alt=""
              className="block h-auto w-full opacity-60"
              style={{ maskImage: "linear-gradient(to bottom, #000 70%, transparent)" }}
            />
          </div>
          <p className="-mb-3 mt-1.5 text-center text-[10px] font-semibold tracking-[0.16em] text-fs-claro/45">
            Versão 0.1b
          </p>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <main className="mx-auto w-full max-w-faixa flex-1 px-4 py-7 lg:px-6">
            <Outlet />
          </main>

          <footer className="nao-imprimir border-t border-border/70 bg-card/50">
            <p className="mx-auto max-w-faixa px-4 py-5 text-[11px] leading-relaxed text-muted-foreground lg:px-6">
              Alíquotas e tratamentos codificados a partir do RICMS de cada UF e do ambiente regulatório
              federal — <b className="text-fs-escuro">confirme com a área TAX antes do uso em produção</b>.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}
